import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ConfirmationModal from 'components/Shared/ConfirmationModal/ConfirmationModal';
import { ConfirmationDialogProps } from 'components/Shared/shared.types';

// isLoading is intentionally excluded so the overlay reflects live async state while copy stays frozen at open time.
type ModalSnapshot = Omit<ConfirmationDialogProps, 'isLoading'>;

type ModalSession = {
  resolve: (value: boolean) => void;
  snapshot: ModalSnapshot;
  confirmed: boolean;
};

// Use this hook to render a confirmation modal for functions like save, discard, delete, and download
// e.g., currently this is used in `Delete Zone`, `Delete Module`, `Publish and Discard ZM`, `Delete Merchant`, `Download Engagement Tables`
const useConfirmModal = (props: ConfirmationDialogProps) => {
  const {
    title,
    description,
    icon,
    theme,
    primaryAction,
    secondaryAction,
    confirmationInput,
    className,
    isLoading,
    actionStyle,
    warningMessage,
    infoBanner,
    primaryActionBadge,
    useSecondaryLinkStyle,
  } = props;
  const [session, setSession] = useState<ModalSession | null>(null);
  const pendingPromiseRef = useRef<Promise<boolean> | null>(null);
  const sawLoadingWhileConfirmedRef = useRef(false);
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const clearPendingPromise = useCallback(() => {
    pendingPromiseRef.current = null;
  }, []);

  const closeSession = useCallback(
    (result: boolean) => {
      setSession((current) => {
        current?.resolve(result);
        return null;
      });
      clearPendingPromise();
      sawLoadingWhileConfirmedRef.current = false;
    },
    [clearPendingPromise],
  );

  const showConfirmModal = useCallback(() => {
    if (pendingPromiseRef.current) {
      return pendingPromiseRef.current;
    }

    const snapshot: ModalSnapshot = {
      title,
      description,
      icon,
      theme,
      primaryAction,
      secondaryAction,
      confirmationInput,
      className,
      actionStyle,
      warningMessage,
      infoBanner,
      primaryActionBadge,
      useSecondaryLinkStyle,
    };

    sawLoadingWhileConfirmedRef.current = false;

    const promise = new Promise<boolean>((resolve) => {
      setSession({ resolve, snapshot, confirmed: false });
    });

    pendingPromiseRef.current = promise;
    return promise;
  }, [
    title,
    description,
    icon,
    theme,
    primaryAction,
    secondaryAction,
    confirmationInput,
    className,
    actionStyle,
    warningMessage,
    infoBanner,
    primaryActionBadge,
    useSecondaryLinkStyle,
  ]);

  const dismissModal = useCallback(() => {
    closeSession(false);
  }, [closeSession]);

  const handleConfirm = useCallback(() => {
    let onConfirm: (() => void) | undefined;

    setSession((current) => {
      if (!current) {
        return null;
      }

      onConfirm = current.snapshot.primaryAction.onClickHandler;
      current.resolve(true);
      return { ...current, confirmed: true };
    });

    onConfirm?.();

    // Macrotask: run after the caller's promise continuation can set isLoading.
    window.setTimeout(() => {
      if (isLoadingRef.current || sawLoadingWhileConfirmedRef.current) {
        return;
      }

      setSession((current) => {
        if (current?.confirmed) {
          clearPendingPromise();
          return null;
        }

        return current;
      });
    }, 0);
  }, [clearPendingPromise]);

  const handleCancel = useCallback(() => {
    let onCancel: (() => void) | undefined;

    setSession((current) => {
      onCancel = current?.snapshot.secondaryAction.onClickHandler;
      current?.resolve(false);
      return null;
    });
    clearPendingPromise();
    sawLoadingWhileConfirmedRef.current = false;
    onCancel?.();
  }, [clearPendingPromise]);

  // After confirm, keep the modal mounted while isLoading so the overlay spinner can render.
  useLayoutEffect(() => {
    if (!session?.confirmed) {
      return;
    }

    if (isLoading) {
      sawLoadingWhileConfirmedRef.current = true;
      return;
    }

    if (sawLoadingWhileConfirmedRef.current) {
      setSession(null);
      clearPendingPromise();
      sawLoadingWhileConfirmedRef.current = false;
    }
  }, [session?.confirmed, isLoading, clearPendingPromise]);

  const ConfirmModal = useCallback(() => {
    if (!session) {
      return null;
    }

    const { snapshot } = session;

    return (
      <ConfirmationModal
        isOpen
        closeModal={dismissModal}
        theme={snapshot.theme}
        icon={snapshot.icon}
        title={snapshot.title}
        description={snapshot.description}
        primaryAction={{
          value: snapshot.primaryAction.value ?? 'Continue',
          onClickHandler: handleConfirm,
        }}
        secondaryAction={{
          value: snapshot.secondaryAction.value ?? 'Cancel',
          onClickHandler: handleCancel,
        }}
        confirmationInput={snapshot.confirmationInput}
        className={snapshot.className}
        isLoading={isLoading}
        actionStyle={snapshot.actionStyle}
        warningMessage={snapshot.warningMessage}
        infoBanner={snapshot.infoBanner}
        primaryActionBadge={snapshot.primaryActionBadge}
        useSecondaryLinkStyle={snapshot.useSecondaryLinkStyle}
      />
    );
  }, [session, isLoading, dismissModal, handleCancel, handleConfirm]);

  return [ConfirmModal, showConfirmModal] as const;
};

export default useConfirmModal;
